/* eslint-disable @typescript-eslint/no-explicit-any */
import { providePrimeNG } from 'primeng/config';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import Material from '@primeng/themes/material';
import { MotifNativeDateTimeModule } from '@ey-xd/ng-motif';

import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  provideZoneChangeDetection,
  importProvidersFrom,
  LOCALE_ID,
} from '@angular/core';
import { provideRouter, TitleStrategy } from '@angular/router';
import { routes } from './app.routes';
import { PageTitleStrategy } from './core/strategies/page-title.strategy';
import {
  provideHttpClient,
  withInterceptorsFromDi,
  HTTP_INTERCEPTORS,
  withFetch,
} from '@angular/common/http';
import { provideNoopAnimations } from '@angular/platform-browser/animations';
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  InteractionStatus,
} from '@azure/msal-browser';
import {
  MsalInterceptor,
  MSAL_INSTANCE,
  MsalInterceptorConfiguration,
  MsalGuardConfiguration,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
} from '@azure/msal-angular';
import { environment } from '../environments/environment';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { CustomHeadersInterceptor } from './core/interceptors/custom-headers.interceptor';
import { IMAGE_CONFIG } from '@angular/common';
import { MessageService } from 'primeng/api';
import { LoadingInterceptor } from '@sa/shared/interceptor/loading.interceptor';
import { ErrorService } from '@sa/shared/services/error-service';

import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { HttpLoggingInterceptor } from './core/interceptors/payload.interceptor';
import { filter, take } from 'rxjs';

const aiConnectionString = environment.applicationInsights.connectionString;
const isAppInsightsConfigured = aiConnectionString
  && !aiConnectionString.startsWith('$')
  && aiConnectionString.includes('InstrumentationKey=');

let appInsights: ApplicationInsights | null = null;

if (isAppInsightsConfigured) {
  appInsights = new ApplicationInsights({
    config: {
      connectionString: aiConnectionString,
      enableAutoRouteTracking: true,
      enableAjaxErrorStatusText: true,
      enableAjaxPerfTracking: true,
      disableCookiesUsage: true,
      cookieCfg: {
        enabled: false,
      },
    },
  });
  appInsights.addTelemetryInitializer((envelope: any) => {
    envelope.tags['ai.cloud.role'] = 'sa-frontend-ui';
    const userName = localStorage.getItem('loggedInUserName') || 'anonymous';
    const correlationId =
      localStorage.getItem('correlationId') || 'no-correlation-id';
    envelope.tags['ai.user.id'] = userName;
    envelope.tags['ai.operation.id'] = correlationId;
  });
  appInsights.loadAppInsights();
}

export { appInsights };

export function msalInitializer(
  msalService: MsalService,
  broadcastService: MsalBroadcastService
): () => Promise<void> {
  return () => {
    return new Promise<void>(resolve => {
      let resolved = false;
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          const accounts = msalService.instance.getAllAccounts();
          if (accounts.length > 0) {
            msalService.instance.setActiveAccount(accounts[0]);
          }
          resolve();
        }
      }, 5000);

      msalService.handleRedirectObservable().subscribe({
        next: result => {
          if (result) {
            msalService.instance.setActiveAccount(result.account);
          }
        },
        error: error => console.error('Redirect error:', error),
        complete: () => {
          broadcastService.inProgress$
            .pipe(
              filter((status: InteractionStatus) => {
                return status === InteractionStatus.None;
              }),
              take(1)
            )
            .subscribe(() => {
              if (!resolved) {
                resolved = true;
                clearTimeout(timeout);
                const accounts = msalService.instance.getAllAccounts();
                if (accounts.length > 0) {
                  msalService.instance.setActiveAccount(accounts[0]);
                }
                resolve();
              }
            });
        },
      });
    });
  };
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.msalConfig.auth.authority,
      redirectUri: '/',
      postLogoutRedirectUri: '/',
    },
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage,
      secureCookies: true,
      storeAuthStateInCookie: false,
    },
    system: {
      preventCorsPreflight: true,
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, string[]>();
  protectedResourceMap.set(
    environment.apiConfig.uri,
    environment.apiConfig.scopes
  );
  if (environment.serviceHealthApiUrl) {
    protectedResourceMap.set(environment.serviceHealthApiUrl, environment.apiConfig.apiScope);
  }

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.apiConfig.scopes],
    },
    loginFailedRoute: '/dashboard',
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    { provide: TitleStrategy, useClass: PageTitleStrategy },
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    {
      provide: APP_INITIALIZER,
      useFactory: msalInitializer,
      deps: [MsalService, MsalBroadcastService],
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: ErrorService,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpLoggingInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },

    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CustomHeadersInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingInterceptor,
      multi: true,
    },
    MessageService,
    { provide: LOCALE_ID, useValue: 'en-GB' },
    provideAnimationsAsync(),
    provideNoopAnimations(),
    importProvidersFrom(MotifNativeDateTimeModule),
    providePrimeNG({
      theme: {
        preset: Material,
        options: {
          prefix: 'p',
          darkModeSelector: 'false',
          cssLayer: false,
        },
      },
      ripple: true,
      csp: {
        nonce: '...',
      },
      translation: {
        aria: {
          firstPageLabel: 'Go to first page',
          prevPageLabel: 'Go to previous page',
          nextPageLabel: 'Go to next page',
          lastPageLabel: 'Go to last page',
          pageLabel: 'Page {page}',
          rowsPerPageLabel: 'Rows per page',
          jumpToPageDropdownLabel: 'Jump to page',
          jumpToPageInputLabel: 'Jump to page',
        },
      },
    }),
    {
      provide: IMAGE_CONFIG,
      useValue: {
        disableImageSizeWarning: true,
        disableImageLazyLoadWarning: true,
      },
    },
  ],
};